import { INQUIRY_SOURCE_LABEL } from '../../modules/telephony/utils/inquirySources';

const DECISION_LABELS = {
  RESOLVED: 'Питання знято',
  CONFIRMED: 'Проблему підтверджено',
  CLARIFY: 'Потрібно уточнити',
};

/** «1 год 05 хв» / «12 хв» / «менше хвилини» */
export function formatDuration(ms) {
  const totalMin = Math.floor(Math.max(0, ms) / 60000);
  if (totalMin < 1) return 'менше хвилини';
  const days = Math.floor(totalMin / (24 * 60));
  const hours = Math.floor((totalMin % (24 * 60)) / 60);
  const minutes = totalMin % 60;
  if (days > 0) return `${days} д ${hours} год`;
  if (hours > 0) return `${hours} год ${String(minutes).padStart(2, '0')} хв`;
  return `${minutes} хв`;
}

/** «Прострочено загалом: 47 хв» — від першої просрочки ланцюжка контролю; null, якщо просрочки ще не було. */
const overdueTotal = (d, now = Date.now()) => {
  const since = d.overdueSince ? new Date(d.overdueSince).getTime() : NaN;
  return Number.isFinite(since) ? `Прострочено загалом: ${formatDuration(now - since)}` : null;
};

/** «На взяття в роботу лишилось: 15 хв» — поточний (новий) цикл після просрочки. */
const nextCycleLeft = (d, now = Date.now()) => {
  const next = d.nextDeadlineAt ? new Date(d.nextDeadlineAt).getTime() : NaN;
  return Number.isFinite(next) ? `На взяття в роботу лишилось: ${formatDuration(next - now)}` : null;
};

/** Що просять зробити менеджера: типове завдання для пінка з дзвінка, текст автора — для ручного. */
const ATTENTION_TASKS = {
  MISSED_CALL: 'передзвонити клієнту та оформити звернення (напрямок, місто або область)',
  ANSWERED_CALL: 'оформити звернення за розмовою (напрямок, місто або область)',
};
const attentionTask = (d) => ATTENTION_TASKS[d.kind] || d.note || 'опрацювати звернення';

const INVOICE_CHANGE_TITLE = {
  ADDED: 'До звернення додано рахунок',
  UNLINKED: 'Рахунок відкріплено від звернення',
  DECLINED: 'Клієнт відмовився від рахунку',
  RESTORED: 'Відмову клієнта від рахунку знято',
};

/** «№123 · КП на гідроциліндр» */
const title = (prefix, d) => `${prefix}${d.number != null ? ` №${d.number}` : ''}`;

/** «ТОВ «Ромашка» · prom.ua · від Коваль Олена» */
function describe(d) {
  return [d.counterpartyName, INQUIRY_SOURCE_LABEL[d.source], d.actorName ? `від ${d.actorName}` : null].filter(Boolean).join(' · ');
}

/**
 * SSE-подія звернення → сповіщення (in-app тост + desktop), або null.
 * Форма — та сама, що в toastForPromEvent: { key, title, body, line2, target, tone, actionLabel }.
 */
export function toastForInquiryEvent(event, data) {
  const target = `/telephony/inquiries/${data.inquiryId}`;
  switch (event) {
    case 'inquiry.assigned':
      return {
        key: `inquiry-assigned:${data.inquiryId}:${Date.now()}`,
        title: title('Вас призначено відповідальним за звернення', data),
        body: data.subject || '',
        line2: [describe(data), data.comment].filter(Boolean).join(' — '),
        target,
        tone: 'info',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.commented':
      return {
        key: `inquiry-commented:${data.eventId || data.inquiryId}:${Date.now()}`,
        title: title('Новий коментар у зверненні', data),
        body: data.subject || '',
        line2: [describe(data), data.comment].filter(Boolean).join(' — '),
        target,
        tone: 'info',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.curator_notified':
      return {
        key: `curator-notified:${data.eventId || data.inquiryId}:${Date.now()}`,
        title: title('Менеджер повідомив куратора', data),
        body: data.subject || '',
        line2: [describe(data), data.typeLabel, data.comment].filter(Boolean).join(' — '),
        target,
        tone: 'info',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.invoice_changed':
      return {
        key: `inquiry-invoice-changed:${data.inquiryId}:${data.kind}:${data.invoiceNumber || ''}`,
        title: title(INVOICE_CHANGE_TITLE[data.kind] || 'Змінилися рахунки звернення', data),
        body: data.subject || '',
        line2: [describe(data), data.invoiceNumber, data.amount != null ? `${data.amount} грн` : null, data.reason].filter(Boolean).join(' — '),
        target,
        tone: data.kind === 'DECLINED' ? 'warning' : 'info',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.auto_shipped':
      return {
        key: `inquiry-auto-shipped:${data.inquiryId}`,
        title: title('Звернення автоматично позначено «Відвантажено»', data),
        body: data.subject || '',
        line2: [describe(data), 'Оплату та документи підтверджено, питань немає'].filter(Boolean).join(' — '),
        target,
        tone: 'info',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.attention':
      return {
        key: `inquiry-attention:${data.inquiryId}:${Date.now()}`,
        title: title(data.kind === 'MISSED_CALL' ? 'Потрібна реакція: пропущений дзвінок' : data.kind === 'ANSWERED_CALL' ? 'Потрібна реакція: розмова без звернення' : 'Потрібна реакція менеджера', data),
        // Що саме хочуть від менеджера — окремим рядком над темою, щоб не зливалось із описом звернення
        body: `Завдання: ${attentionTask(data)}`,
        line2: [data.subject, describe(data), data.kind === 'MANUAL' ? null : data.note].filter(Boolean).join(' · '),
        target,
        tone: 'danger',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.queued':
      return {
        key: `inquiry-queued:${data.inquiryId}:${Date.now()}`,
        title: title('Нове звернення в черзі підрозділу', data),
        body: data.subject || '',
        line2: [describe(data), data.comment].filter(Boolean).join(' — ') || 'Можна взяти в роботу',
        target,
        tone: 'warning',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.control.warning':
      return {
        // Один тост на (звернення, попередження): 5 і 1 хв не затирають одне одного
        key: `inquiry-control-warning:${data.inquiryId}:${data.minutes}`,
        title: `${data.minutes === 1 ? 'Лишилась 1 хвилина' : `Лишилось ${data.minutes} хвилин`} ${data.phase === 'WAITING_ASSIGNMENT' ? 'щоб узяти звернення в роботу' : 'до першої реакції'}`,
        body: data.subject || '',
        line2: [title('Звернення', data), describe(data), overdueTotal(data)].filter(Boolean).join(' · '),
        target,
        tone: 'warning',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.control.expired':
      return {
        // Просрочки повторюються з новим дедлайном — ключ окремий на кожну
        key: `inquiry-control-expired:${data.inquiryId}:${data.deadlineAt}`,
        title: data.phase === 'WAITING_ASSIGNMENT' ? 'Прострочено: звернення ніхто не взяв в роботу' : 'Прострочено: немає першої реакції, звернення повернуто в чергу',
        body: data.subject || '',
        line2: [title('Звернення', data), describe(data), overdueTotal(data), nextCycleLeft(data)].filter(Boolean).join(' · '),
        target,
        tone: 'warning',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.callback.due':
      return {
        key: `inquiry-callback-due:${data.inquiryId}:${data.nextContactAt}`,
        title: title('Час передзвонити клієнту', data),
        body: data.subject || '',
        line2: describe(data),
        target,
        tone: 'warning',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.callback.result':
      return {
        // Повтори (30 хв → кожні 2 год) не затирають один одного: ключ із номером спроби
        key: `inquiry-callback-result:${data.inquiryId}:${data.nextContactAt}:${data.attempt}`,
        title: title('Внесіть результат контакту', data),
        body: data.subject || '',
        line2: [describe(data), 'Строк передзвону минув, а в зверненні немає запису про розмову'].filter(Boolean).join(' — '),
        target,
        tone: 'danger',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.escalated':
      return {
        key: `escalated:${data.eventId}`,
        title: 'Питання куратору',
        body: data.subject || '',
        line2: '',
        target,
        tone: 'warning',
        actionLabel: 'Відкрити звернення',
      };
    case 'inquiry.decided':
      return {
        key: `decided:${data.eventId}`,
        title: `Рішення куратора: ${DECISION_LABELS[data.decision] || data.decision || ''}`,
        body: data.subject || '',
        line2: '',
        target,
        tone: data.decision === 'CONFIRMED' ? 'warning' : 'info',
        actionLabel: 'Відкрити звернення',
      };
    default:
      return null;
  }
}
